<script lang="ts">
  import { addCredits } from '../../lib/components/map/credits';
  import { onDestroy, onMount } from 'svelte';
  import * as maplibregl from 'maplibre-gl';
  import { api } from '../../lib/api/client';
  import type { Explorer, MapInfo, TravelMode } from '../../lib/api/types';
  import { followTheme, northUpMap, styleFor } from '../../lib/components/map/theme';
  import { palette } from '../../lib/utils/palette';
  import { tileRing } from '../../lib/utils/tiles';
  import { decodePolyline } from '../../lib/utils/training';
  import { count } from '../../lib/utils/insights';
  import { t } from '../../lib/i18n/index.ts';
  import { modeLabel } from './format';

  // Explorer is every kept trip of the member's on one map (spec 005): routes in one low-opacity line layer, so roads taken often draw
  // brighter, flights thin and dashed, and the zoom-14 explorer tiles the
  // routes pass through as one fill layer. Tiles first reached this month
  // are outlined as well as colored, and the largest square is outlined.
  type Period = 'month' | 'year' | 'pick' | 'all';
  const PERIODS: { id: Period; name: string }[] = [
    { id: 'month', name: t('timelineThisMonth') },
    { id: 'year', name: t('timelineThisYear') },
    { id: 'pick', name: t('timelineAYear') },
    { id: 'all', name: t('timelineAllTime') },
  ];
  const MODES: TravelMode[] = ['walking', 'running', 'cycling', 'driving', 'bus', 'train', 'flight', 'other'];

  const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
  const now = new Date();
  let period = $state<Period>('all');
  let year = $state(now.getFullYear() - 1);
  let mode = $state<TravelMode | ''>('');
  let data = $state<Explorer | null>(null);
  let loading = $state(false);
  let error = $state('');
  // seq drops an answer that a later filter has already replaced.
  let seq = 0;

  let el: HTMLDivElement | undefined = $state();
  let map: maplibregl.Map | null = null;
  let stopTheme: (() => void) | null = null;
  let ready = false;

  // range is the from and to days of the chosen period, empty for all time.
  function range(p: Period, y: number): string {
    const pad = (n: number) => String(n).padStart(2, '0');
    if (p === 'month') {
      const m = now.getMonth() + 1;
      const last = new Date(now.getFullYear(), m, 0).getDate();
      return `&from=${now.getFullYear()}-${pad(m)}-01&to=${now.getFullYear()}-${pad(m)}-${pad(last)}`;
    }
    if (p === 'year') return `&from=${now.getFullYear()}-01-01&to=${now.getFullYear()}-12-31`;
    if (p === 'pick') return `&from=${y}-01-01&to=${y}-12-31`;
    return '';
  }

  $effect(() => {
    const q = `tz=${encodeURIComponent(tz)}${range(period, year)}${mode ? `&mode=${mode}` : ''}`;
    const mine = ++seq;
    loading = true;
    api.get<Explorer>(`/me/timeline/explorer?${q}`)
      .then((got) => {
        if (mine !== seq) return;
        data = got;
        error = '';
        draw(true);
      })
      .catch((e) => { if (mine === seq) error = e.message; })
      .finally(() => { if (mine === seq) loading = false; });
  });

  function draw(fit: boolean) {
    if (!map || !ready || !data) return;
    const z = data.tiles.zoom;
    const lines = (encoded: string[]) => ({
      type: 'FeatureCollection' as const,
      features: encoded.map(decodePolyline).filter((l) => l.length > 1)
        .map((coordinates) => ({ type: 'Feature' as const, properties: {}, geometry: { type: 'LineString' as const, coordinates } })),
    });
    (map.getSource('routes') as maplibregl.GeoJSONSource).setData(lines(data.routes.map((r) => r.line)));
    (map.getSource('flights') as maplibregl.GeoJSONSource).setData(lines(data.flights));
    (map.getSource('tiles') as maplibregl.GeoJSONSource).setData({
      type: 'FeatureCollection',
      features: data.tiles.cells.map(([x, y, fresh]) => ({
        type: 'Feature', properties: { new: fresh === 1 }, geometry: { type: 'Polygon', coordinates: [tileRing(x, y, z)] },
      })),
    });
    const at = data.tiles.max_square_at;
    (map.getSource('square') as maplibregl.GeoJSONSource).setData({
      type: 'FeatureCollection',
      features: at && data.tiles.max_square > 0
        ? [{ type: 'Feature', properties: {}, geometry: { type: 'LineString', coordinates: tileRing(at[0], at[1], z, data.tiles.max_square) } }]
        : [],
    });
    if (fit && data.bounds) {
      const [w, s, e, n] = data.bounds;
      map.fitBounds([[w, s], [e, n]], { padding: 40, maxZoom: 14, duration: 0 });
    }
  }

  onMount(async () => {
    let info: MapInfo | null = null;
    try {
      info = await api.get<MapInfo>('/map');
    } catch {
      // A blank background still shows the routes and tiles.
    }
    if (!el) return;
    map = northUpMap({ container: el, style: styleFor(info), center: [-98.5, 39.8], zoom: 3, attributionControl: false });
    addCredits(map, info, 'other');
    map.addControl(new maplibregl.NavigationControl({ showCompass: false }), 'top-right');
    stopTheme = followTheme(map, info, () => {
      const empty: GeoJSON.FeatureCollection = { type: 'FeatureCollection', features: [] };
      for (const id of ['tiles', 'routes', 'flights', 'square']) map!.addSource(id, { type: 'geojson', data: empty });
      map!.addLayer({ id: 'tiles', type: 'fill', source: 'tiles',
        paint: { 'fill-color': ['case', ['get', 'new'], palette('peach'), palette('mauve')], 'fill-opacity': ['case', ['get', 'new'], 0.4, 0.2] } });
      map!.addLayer({ id: 'tiles-new', type: 'line', source: 'tiles', filter: ['get', 'new'],
        paint: { 'line-color': palette('peach'), 'line-width': 1.5 } });
      map!.addLayer({ id: 'routes', type: 'line', source: 'routes', layout: { 'line-join': 'round', 'line-cap': 'round' },
        paint: { 'line-color': palette('blue'), 'line-opacity': 0.3, 'line-width': ['interpolate', ['linear'], ['zoom'], 8, 1.5, 14, 3, 18, 6] } });
      map!.addLayer({ id: 'flights', type: 'line', source: 'flights',
        paint: { 'line-color': palette('pink'), 'line-opacity': 0.7, 'line-width': 1, 'line-dasharray': [3, 3] } });
      map!.addLayer({ id: 'square', type: 'line', source: 'square',
        paint: { 'line-color': palette('text'), 'line-width': 2.5, 'line-dasharray': [2, 1.5] } });
      const first = !ready;
      ready = true;
      draw(first);
    });
  });

  onDestroy(() => {
    stopTheme?.();
    map?.remove();
  });

  const empty = $derived(data != null && data.routes.length === 0 && data.flights.length === 0);
</script>

<div class="flex-1 flex flex-col md:flex-row min-h-0 overflow-y-auto md:overflow-hidden">
  <aside class="w-full md:w-80 shrink-0 md:border-r p-4 space-y-4 md:overflow-y-auto" style="border-color: var(--theme-surface0); background: var(--theme-mantle);">
    <div class="flex flex-wrap gap-2">
      <select class="input flex-1 min-w-0" bind:value={period} aria-label={t('timelinePeriod')}>
        {#each PERIODS as p (p.id)}<option value={p.id}>{p.name}</option>{/each}
      </select>
      {#if period === 'pick'}
        <input type="number" class="input w-24" min="2000" max={now.getFullYear()} bind:value={year} aria-label={t('timelineYear')} />
      {/if}
      <select class="input flex-1 min-w-0" bind:value={mode} aria-label={t('timelineWayOfTravel')}>
        <option value="">{t('timelineEveryWay')}</option>
        {#each MODES as m (m)}<option value={m}>{modeLabel(m)}</option>{/each}
      </select>
    </div>
    {#if error}
      <p class="text-sm" style="color: var(--theme-error);">{error}</p>
    {:else if data}
      <dl class="grid grid-cols-2 gap-3" aria-live="polite">
        <div>
          <dt class="text-xs text-text-muted">{t('timelineExplorerTiles')}</dt>
          <dd class="text-xl font-semibold tabular-nums">{count(data.tiles.count)}</dd>
        </div>
        <div>
          <dt class="text-xs text-text-muted">{t('timelineNewThisMonth')}</dt>
          <dd class="text-xl font-semibold tabular-nums">{count(data.tiles.new_this_month)}</dd>
        </div>
        <div>
          <dt class="text-xs text-text-muted">{t('timelineLargestSquare')}</dt>
          <dd class="text-xl font-semibold tabular-nums" aria-label={t('timelineSquareName', { n: data.tiles.max_square })}>{t('timelineSquareSize', { n: count(data.tiles.max_square) })}</dd>
        </div>
        <div>
          <dt class="text-xs text-text-muted">{t('timelineLargestCluster')}</dt>
          <dd class="text-xl font-semibold tabular-nums">{t('timelineTileCount', { n: data.tiles.max_cluster })}</dd>
        </div>
      </dl>
      <p class="text-xs text-text-muted">{data.flights.length
        ? t('timelineRouteFlightCount', { routes: data.routes.length, flights: data.flights.length })
        : t('timelineRouteCount', { routes: data.routes.length })}</p>
    {/if}
    <ul class="space-y-1.5 text-xs text-text-muted" aria-label={t('timelineLegend')}>
      <li class="flex items-center gap-2">
        <svg width="20" height="10" aria-hidden="true"><line x1="0" x2="20" y1="5" y2="5" stroke="var(--theme-blue)" stroke-width="3" stroke-linecap="round" opacity="0.6" /></svg>
        {t('timelineLegendRoutes')}
      </li>
      <li class="flex items-center gap-2">
        <svg width="20" height="10" aria-hidden="true"><line x1="0" x2="20" y1="5" y2="5" stroke="var(--theme-pink)" stroke-width="1.5" stroke-dasharray="3 3" /></svg>
        {t('timelineLegendFlights')}
      </li>
      <li class="flex items-center gap-2">
        <svg width="20" height="12" aria-hidden="true"><rect x="4" y="0" width="12" height="12" fill="var(--theme-mauve)" opacity="0.35" /></svg>
        {t('timelineLegendTile')}
      </li>
      <li class="flex items-center gap-2">
        <svg width="20" height="12" aria-hidden="true"><rect x="4.75" y="0.75" width="10.5" height="10.5" fill="var(--theme-peach)" fill-opacity="0.5" stroke="var(--theme-peach)" stroke-width="1.5" /></svg>
        {t('timelineLegendNewTile')}
      </li>
      <li class="flex items-center gap-2">
        <svg width="20" height="12" aria-hidden="true"><rect x="3" y="1" width="14" height="10" fill="none" stroke="var(--theme-text)" stroke-width="1.5" stroke-dasharray="3 2" /></svg>
        {t('timelineLargestSquare')}
      </li>
    </ul>
    <p class="text-xs text-text-muted">{t('timelineExplorerPrivate')}</p>
  </aside>
  <div class="relative flex-1 min-h-[60vh] min-w-0" aria-busy={loading}>
    <div bind:this={el} class="absolute inset-0" role="img"
      aria-label={data ? t('timelineExplorerMapCounts', { routes: data.routes.length, tiles: data.tiles.count }) : t('timelineExplorerMap')}></div>
    {#if empty}
      <div class="absolute inset-0 flex items-center justify-center pointer-events-none">
        <p class="card px-4 py-3 text-sm">{t('timelineExplorerEmpty')}</p>
      </div>
    {/if}
  </div>
</div>
